import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listLeads,
  getLead,
  createLead,
  startLeadProgress,
  updateLead,
  deleteLead,
  qualifyLead,
  disqualifyLead,
  listProductInterests,
  getProductInterest,
  createProductInterest,
  deferProductInterest,
  reactivateProductInterest,
  closeProductInterest,
  listActivities,
  logActivity,
  listConsents,
  captureConsent,
  withdrawConsent,
  getCustomer360,
} from './network';
import type { LeadStatus, UpdateLeadDto } from './types';

const keys = {
  all: ['crm'] as const,
  leads: () => [...keys.all, 'leads'] as const,
  leadList: (params?: Record<string, unknown>) => [...keys.leads(), 'list', params] as const,
  lead: (id: string) => [...keys.leads(), id] as const,
  productInterests: () => [...keys.all, 'product-interests'] as const,
  productInterestList: (params?: Record<string, unknown>) => [...keys.productInterests(), 'list', params] as const,
  productInterest: (id: string) => [...keys.productInterests(), id] as const,
  activities: (partyId: string) => [...keys.all, 'activities', partyId] as const,
  consents: (partyId: string) => [...keys.all, 'consents', partyId] as const,
  customer360: (partyId: string) => [...keys.all, '360', partyId] as const,
};

// --- Leads Hooks ---
export function useLeads(params?: {
  status?: LeadStatus;
  ownerId?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: keys.leadList(params),
    queryFn: () => listLeads(params),
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: keys.lead(id),
    queryFn: () => getLead(id),
    enabled: !!id,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.leads() });
    },
  });
}

export function useStartLeadProgress(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => startLeadProgress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.leads() });
      queryClient.invalidateQueries({ queryKey: keys.lead(id) });
    },
  });
}

export function useQualifyLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { qualification: Record<string, unknown> }) => qualifyLead(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.leads() });
      queryClient.invalidateQueries({ queryKey: keys.lead(id) });
    },
  });
}

export function useDisqualifyLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) => disqualifyLead(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.leads() });
      queryClient.invalidateQueries({ queryKey: keys.lead(id) });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLeadDto }) => updateLead(id, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.leads() });
      queryClient.invalidateQueries({ queryKey: keys.lead(variables.id) });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => deleteLead(id, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.leads() });
      queryClient.invalidateQueries({ queryKey: keys.lead(variables.id) });
    },
  });
}

// --- Product Interests Hooks ---
export function useProductInterests(params?: {
  partyId?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: keys.productInterestList(params),
    queryFn: () => listProductInterests(params),
  });
}

export function useProductInterest(id: string) {
  return useQuery({
    queryKey: keys.productInterest(id),
    queryFn: () => getProductInterest(id),
    enabled: !!id,
  });
}

export function useCreateProductInterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProductInterest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.productInterests() });
    },
  });
}

export function useDeferProductInterest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason?: string }) => deferProductInterest(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.productInterests() });
      queryClient.invalidateQueries({ queryKey: keys.productInterest(id) });
    },
  });
}

export function useReactivateProductInterest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => reactivateProductInterest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.productInterests() });
      queryClient.invalidateQueries({ queryKey: keys.productInterest(id) });
    },
  });
}

export function useCloseProductInterest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) => closeProductInterest(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.productInterests() });
      queryClient.invalidateQueries({ queryKey: keys.productInterest(id) });
    },
  });
}

// --- Activities Hooks ---
export function useCrmActivities(partyId: string, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...keys.activities(partyId), params],
    queryFn: () => listActivities(partyId, params),
    enabled: !!partyId,
  });
}

export function useLogCrmActivity(partyId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logActivity,
    onSuccess: () => {
      if (partyId) {
        queryClient.invalidateQueries({ queryKey: keys.activities(partyId) });
        queryClient.invalidateQueries({ queryKey: keys.customer360(partyId) });
      }
    },
  });
}

// --- Consents Hooks ---
export function useCrmConsents(partyId: string) {
  return useQuery({
    queryKey: keys.consents(partyId),
    queryFn: () => listConsents(partyId),
    enabled: !!partyId,
  });
}

export function useCaptureConsent(partyId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: captureConsent,
    onSuccess: () => {
      if (partyId) {
        queryClient.invalidateQueries({ queryKey: keys.consents(partyId) });
        queryClient.invalidateQueries({ queryKey: keys.customer360(partyId) });
      }
    },
  });
}

export function useWithdrawConsent(partyId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => withdrawConsent(id),
    onSuccess: () => {
      if (partyId) {
        queryClient.invalidateQueries({ queryKey: keys.consents(partyId) });
        queryClient.invalidateQueries({ queryKey: keys.customer360(partyId) });
      }
    },
  });
}

// --- Customer 360 Hook ---
export function useCustomer360(partyId: string) {
  return useQuery({
    queryKey: keys.customer360(partyId),
    queryFn: () => getCustomer360(partyId),
    enabled: !!partyId,
  });
}

import * as React from 'react';

export function useCrmContact(id: string) {
  const leadQuery = useLead(id);
  const partyId = leadQuery.data?.partyId ?? '';
  const c360Query = useCustomer360(partyId);

  const data = React.useMemo(() => {
    if (!leadQuery.data) return undefined;
    const c360 = c360Query.data;
    return {
      id: leadQuery.data.id,
      partyId: leadQuery.data.partyId,
      name: c360?.name || c360?.party?.canonicalName || leadQuery.data.partyName || '',
      fullName: c360?.name || c360?.party?.canonicalName || leadQuery.data.partyName || '',
      mobile: c360?.phone || c360?.party?.primaryMobile || leadQuery.data.partyMobile || '',
      mobileAlt: '',
      email: c360?.email || c360?.party?.email || '',
      addressLine1: leadQuery.data.primaryAddress?.addressLine1 || '',
      city: leadQuery.data.primaryAddress?.city || '',
      state: leadQuery.data.primaryAddress?.stateCode || '',
      pincode: leadQuery.data.primaryAddress?.pincode || '',
      fatherOrSpouse: '',
      dateOfBirth: '',
      gender: '',
      status: leadQuery.data.status,
    };
  }, [leadQuery.data, c360Query.data]);

  return {
    ...leadQuery,
    isLoading: leadQuery.isLoading || (!!partyId && c360Query.isLoading),
    data,
  };
}

export function useCrmPipelineSummary(_params?: { orgUnitId?: string; from?: string; to?: string }) {
  const leadsQuery = useLeads({ page: 1, limit: 200 });
  const data = React.useMemo(() => {
    const leads = Array.isArray(leadsQuery.data) ? leadsQuery.data : (leadsQuery.data?.data ?? []);
    const prospects = leads.filter((l) => l.status === 'NEW').length;
    const activeLeads = leads.filter((l) => l.status === 'IN_PROGRESS' || l.status === 'QUALIFIED').length;
    const converted = leads.filter((l) => l.status === 'CONVERTED').length;
    const lost = leads.filter((l) => l.status === 'DISQUALIFIED').length;
    return {
      prospectCount: prospects,
      leadCount: activeLeads,
      convertedThisMonth: converted,
      lostThisMonth: lost,
      funnelStages: [
        { stage: 'PROSPECT' as const, count: prospects },
        { stage: 'LEAD' as const, count: activeLeads },
        { stage: 'CONVERTED' as const, count: converted },
        { stage: 'LOST' as const, count: lost },
      ],
    };
  }, [leadsQuery.data]);

  return {
    ...leadsQuery,
    data,
  };
}
